import { Component, EventEmitter, Inject, Output, PLATFORM_ID } from '@angular/core';
import { Icons } from '../../../shared/helpers/icons';
import { LucideAngularModule } from 'lucide-angular';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Router, RouterOutlet } from '@angular/router';
import { AssessTeamComponent } from '../../components/assess-team/assess-team.component';
import { ReviewRequestComponent } from '../../components/review-request/review-request.component';
import { IAssessCase } from '../../../shared/models/assessCase';
import { UserDataService } from '../../../shared/services/auth/userData.service';
import { AssessCaseService } from '../../../shared/services/assessCase.service';
import { EStage, EStatus } from '../../../shared/models/types.enum';
import { FormUtils } from '../../../shared/helpers/formUtils';
import { SpinnerComponent } from '../../components/spinner/spinner.component';
import { PopupMessageComponent } from '../../components/popup-message/popup-message.component';
import { ReviewCancelComponent } from '../../components/review-cancel/review-cancel.component';
import { FormsModule } from '@angular/forms';
import { NgxPaginationModule } from 'ngx-pagination';
import { IThreatAssessmentTeam } from '../../../shared/models/contact';
declare var initFlowbite: any;

@Component({
  selector: 'app-assessments-dashboard',
  standalone: true,
  imports: [
    FormsModule,
    CommonModule,
    LucideAngularModule,
    AssessTeamComponent,
    ReviewRequestComponent,
    ReviewCancelComponent,
    SpinnerComponent,
    PopupMessageComponent,
    NgxPaginationModule
  ],
  templateUrl: './assessments-dashboard.component.html',
  styleUrl: './assessments-dashboard.component.css'
})
export class AssessmentsDashboardComponent {

  icons = Icons;
  stageEnum = EStage;
  statusEnum = EStatus;
  currentBuildingId: string | null = null;
  currentUser: IThreatAssessmentTeam | null = null;
  searchQuery: string = '';
  assessmentCase: IAssessCase[] = [];
  filteredAssessmentCases: IAssessCase[] = [];
  selectedCase: IAssessCase | null = null;
  messageModal: string | null = null;
  popupMessage: string | null = null;
  page = 1;
  itemsPerPage = 10;

  sortOrder: boolean = true;
  showTeam: boolean = false;
  showReviewRequest: boolean = false;
  showReviewModal: boolean = false;
  showCancelReview: boolean = false;
  loading: boolean = false;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private router: Router,
    private userDataService: UserDataService,
    private assessCaseService: AssessCaseService,
    private formUtils: FormUtils
  ) {}

  ngOnInit(): void { 
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.currentUser = this.userDataService.getCurrentUser();
    this.loadAssessmentsCases();
  }

  loadAssessmentsCases(): void {
    // get all case and filter by current stage at first loading
    if (this.currentBuildingId) {
      this.loading = true;
      this.assessCaseService.getBuildingAssessCases(this.currentBuildingId).subscribe({
        next: (response) => {
          this.loading = false;
          if (!response.message) {
            this.assessmentCase = response.filter((item: IAssessCase) => item.current_stage === EStage.Assessments);
            if (isPlatformBrowser(this.platformId)) { // refresh flowbite effects (tooltip) only in browser
              setTimeout(() => {
                initFlowbite();
              }, 100);
            }
            this.filteredAssessmentCases = this.assessmentCase.length > 0 ? this.assessmentCase.reverse() : [];
          }
        },
        error: (error) => {
          this.loading = false;
          console.error('Error fetching building assessments cases:', error);
          this.popupMessage = error.message
          this.messageModal = 'unsuccess';
        }
      });
    } else {
      return
    }
  }

  sortList(key: keyof typeof this.filteredAssessmentCases[0]): void {
    this.sortOrder = !this.sortOrder;
    this.filteredAssessmentCases.sort((a, b) => {
      const valueA = a[key]?.toString().toLowerCase() || '';
      const valueB = b[key]?.toString().toLowerCase() || '';

      if (valueA < valueB) {
        return this.sortOrder ? -1 : 1;
      }
      if (valueA > valueB) {
        return this.sortOrder ? 1 : -1;
      }
      return 0;
    });
  }

  filterAssessCases(): void {
    this.filteredAssessmentCases = this.assessmentCase.filter((caseItem) => {
      return (
        caseItem.case_id.toLowerCase()
          .includes(this.searchQuery.toLowerCase()) ||
        caseItem.detail_of_situation?.toLowerCase()
          .includes(this.searchQuery.toLowerCase()) ||
        caseItem.created_by_name?.toLowerCase()
          .includes(this.searchQuery.toLowerCase())
      );
    });
  }

  openModal(type: string, clickedCase?: IAssessCase): void {
    if (type === 'team') {
      this.showTeam = true;
    } else if (type === 'review request') {
      this.showReviewRequest = true;
    } else if (type === 'review modal') {
      this.showReviewModal = true;
    } else if (type === 'cancel review') {
      this.showCancelReview = true;
    }
    this.selectedCase = clickedCase ? clickedCase : null;
  }

  closeModal(type: string): void {
    if (type === 'team') {
      this.showTeam = false;
    } else if (type === 'review request') {
      this.showReviewRequest = false;
    } else if (type === 'review modal') {
      this.showReviewModal = false;
    } else if (type === 'cancel review') {
      this.showCancelReview = false;
    }
    this.selectedCase = null;
  }
  
  handleSave(message: {type: string, message: string}, actionType: string): void {
    if (actionType === 'team') {
      this.closeModal('team');
    } else if (actionType === 'review request') {
      this.closeModal('review request');
    } else if (actionType === 'cancel review') {
      this.closeModal('cancel review');
    }
    if (message.type === 'success') {
      this.loadAssessmentsCases();
      this.messageModal = 'success';
      this.popupMessage = message.message;
      
    } else if (message.type === 'failed') {
      this.messageModal = 'unsuccess';
      this.popupMessage = message.message;
    } 
  }

  goToPage(pageName: string, caseId: string): void {
    this.router.navigate(['/assessments/' + pageName + '/' + caseId]);
  }

  getTooltip(fieldValue: number, context: string) {
    return this.formUtils.getTooltipContent(fieldValue, context);
  }

  isStageReviewPending(clickedCase: IAssessCase): boolean {
    if (clickedCase.full_team_threat_assessment === this.statusEnum.ReviewPending || 
      clickedCase.witness_interview === this.statusEnum.ReviewPending || 
      clickedCase.teacher_staff_interview === this.statusEnum.ReviewPending || 
      clickedCase.student_of_concern_interview === this.statusEnum.ReviewPending || 
      clickedCase.parent_interview === this.statusEnum.ReviewPending) 
    {
      return true;
    }
    return false;
  }

  isAllRequiredFormsUndone(selectedCase: IAssessCase): boolean {
    if (selectedCase.threat_assessment_team_status === this.statusEnum.FormUndone ||
      selectedCase.full_team_threat_assessment === this.statusEnum.FormUndone
    ) {
      return true;
    }   
    return false;
  }

  isUserInTeam(selectedCase: IAssessCase): boolean {
    return this.currentUser?.case_ids?.includes(selectedCase.case_id) ?? false;
  }
  
  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = null;
  }
}
